// meter-rule-engine.js — pure evaluator for meter rules (Brief 055).
//
// Consumes the meter rule data layer (Brief 054, src/data/meter-rules-loader.js)
// and turns a learner's meter answer into a required-playcount adjustment for an
// affected item. It is a PURE state machine per CLAUDE.md Rule #1: it imports no
// DOM, no storage, no screens. The orchestration entrypoint (applyMeterResult)
// receives its side effects as injected `deps` so the engine itself never
// touches IndexedDB.
//
// iOS REFERENCES (the canonical behaviour this mirrors):
//   • KFEDialoCompreMeterVC.m:425-436 — two thresholds, `answer1 <= scoreThreshold`.
//   • KFEListeningMeterVC.m:314-323   — one threshold, `7 - selectedIndex < scoreThreshold`
//                                       (i.e. STRICT `<`, since answer1 == 7 - selectedIndex).
//   • KFEMetersDatabaseApi.m:29-101   — dual function: every meter answer is recorded
//                                       (telemetry), but the required_play bump only runs
//                                       for Dialog + Listening WITH an affected item.
//   • KFEBrainSoakingFamMetVC.m:367-378 — BrainSoaking zeroes affectedItemId and all
//                                       thresholds → telemetry only, never adjusts.
//
// Dual-function dispatch (Brief 054 finding):
//   Function A — playcount adjuster (affectedItemId populated): apply the SQL-equivalent
//                `required_play = max(required_play, completed) + delta`.
//   Function B — telemetry only (affectedItemId null): no playcount change. BrainSoaking
//                is always B; un-configured Dialog/Listening templates (null
//                affectedItemId) are B as well.
//
// Reporting reading (Brief 185 — iOS reporting-rework convergence, pinned against
// KFE_Reporting_Rework_Chris @ 9a338a3c). iOS records EVERY meter answer as one
// row in learner_meter_answers (KFEMetersDatabaseApi.m:55 — the INSERT runs for
// all three meter types BEFORE the required_play branch), carrying the new
// content links: affected_item_id (gating target, Brief 003) and measured_item_id
// (the preceding completed content the reading is ABOUT, Brief 018). So the engine
// now records a reading for BOTH functions — A and B — not just B. Pre-185 only
// Function B recorded, leaving every Dialog/Listening (Function A) reading absent
// from reporting. The bump still runs for Function A only.

/** @typedef {import("../data/meter-rules-loader.js").MeterRule} MeterRule */

/**
 * iOS KFEMeteringType codes (KFEMetersDatabaseApi.h:11-15): the numeric `type`
 * column on learner_meter_answers. Web rule.meterType strings map to these so the
 * recorded reading matches the iOS reporting schema's `type` field.
 */
const METER_TYPE_CODE = { listening: 1, "dialog-comp": 2, brainsoaking: 3 };

/**
 * Compute the required-playcount delta for a Function-A meter answer. Returns
 * the first matching threshold's `playcountIncrease`, or 0 when no threshold
 * matches / the rule is null / the rule carries only null (template) thresholds.
 *
 * Comparison operator is iOS-canonical per meter type:
 *   • listening   → STRICT `<`  (KFEListeningMeterVC.m:314)
 *   • dialog-comp → `<=`        (KFEDialoCompreMeterVC.m:425)
 *   • brainsoaking → has no thresholds (all-null) → always 0.
 *
 * Thresholds are walked in stored order; the data layer emits them ascending so
 * the first hit is the tightest applicable bump (mirrors the iOS if/else-if
 * cascade). `answer2` is unused — no iOS meter keys its playcount off the second
 * axis — but the parameter is kept for signature parity with applyMeterResult.
 *
 * @param {MeterRule | null | undefined} meterRule
 * @param {number} answer1
 * @param {number} [answer2]
 * @returns {number} playcount delta (≥ 0)
 */
export function computeAdjustment(meterRule, answer1, answer2) {
  if (!meterRule || !Array.isArray(meterRule.thresholds)) return 0;
  const strict = meterRule.meterType === "listening";
  for (const t of meterRule.thresholds) {
    if (!t || t.score == null || t.playcountIncrease == null) continue;
    const hit = strict ? answer1 < t.score : answer1 <= t.score;
    if (hit) return t.playcountIncrease;
  }
  return 0;
}

/**
 * @typedef {object} MeterReading
 * @property {number} meterItemId            the meter widget's own id (iOS `item_id`).
 * @property {number|null} meterType         iOS `type` code (1/2/3); null if unmapped.
 * @property {number} answer1
 * @property {number|null} answer2
 * @property {number|null} affectedItemId    gating target (iOS `affected_item_id`, Brief 003).
 * @property {number|null} measuredItemId    preceding measured content (iOS `measured_item_id`, Brief 018).
 *
 * @typedef {object} MeterResultDeps
 * @property {(itemId: number, delta: number) => unknown} applyPlaycountAdjustment Function A side effect.
 * @property {(reading: MeterReading) => unknown} recordMeterTelemetry Reporting reading recorder (every answer).
 *
 * @typedef {object} MeterResult
 * @property {"A"|"B"|"none"} fn    which function ran.
 * @property {number} delta         playcount delta applied (0 for B / none).
 * @property {boolean} isPass       true when no bump was warranted (Function A only).
 */

/**
 * Orchestration entrypoint. ALWAYS records one reporting reading (iOS records
 * every answer — Brief 185), then branches on `meterRule.affectedItemId == null`:
 *   • populated → Function A: compute the delta and (if > 0) apply the playcount bump.
 *   • null      → Function B: no playcount change (the reading is still recorded).
 *
 * The engine never imports storage — both side effects are injected via `deps`
 * so callers (and tests) wire them to learner-store or to spies. `measuredItemId`
 * is the just-completed preceding content that triggered the meter (Brief 107
 * positional model) — the iOS `measured_item_id` attribution link; the engine
 * receives it from the host (it is not in the rule).
 *
 * @param {MeterRule | null | undefined} meterRule
 * @param {number} answer1
 * @param {number | null} answer2
 * @param {MeterResultDeps} deps
 * @param {number | null} [measuredItemId=null] preceding completed content (iOS measured_item_id).
 * @returns {Promise<MeterResult>}
 */
export async function applyMeterResult(meterRule, answer1, answer2, deps, measuredItemId = null) {
  if (!meterRule) return { fn: "none", delta: 0, isPass: true };

  // Record the reading for BOTH functions (iOS-faithful — every answer is stored).
  await deps.recordMeterTelemetry({
    meterItemId: meterRule.itemId,
    meterType: METER_TYPE_CODE[meterRule.meterType] ?? null,
    answer1,
    answer2: answer2 ?? null,
    affectedItemId: meterRule.affectedItemId ?? null,
    measuredItemId: measuredItemId ?? null,
  });

  // Function B — reading only, no gating target.
  if (meterRule.affectedItemId == null) {
    return { fn: "B", delta: 0, isPass: true };
  }

  // Function A — playcount adjuster (in addition to the reading above).
  const delta = computeAdjustment(meterRule, answer1, answer2 ?? undefined);
  if (delta > 0) await deps.applyPlaycountAdjustment(meterRule.affectedItemId, delta);
  return { fn: "A", delta, isPass: delta === 0 };
}
